import { HandoffMetric } from '../../../../handoff-hub.types';
import { ChecklistMetricItem } from '../handoff-checklist-v2/handoff-checklist-v2.types';
import {
  mapStatusToResult,
  mapResultToStatus,
  deriveOverallStatus,
  mapChecklistMetricsToHandoffMetrics,
  mapSnapshotMetricsToChecklist,
} from '../handoff-checklist-v2/handoff-checklist-v2.helpers';
import { VOICE_AIR_METRIC_DEFINITIONS } from './voice-air-metric-definitions';
import { MetricEvalContext } from './voice-air-metrics.types';

export {
  mapStatusToResult,
  mapResultToStatus,
  deriveOverallStatus,
  mapChecklistMetricsToHandoffMetrics,
};

export type VoiceAirReasonKey =
  // voice-air-flags
  | 'air_customization_flags_active'
  | 'ai_voice_cf_inactive'
  | 'auto_missed_call_message_cf_inactive'
  | 'missed_call_notification_ff_inactive'
  // truelark-business-id
  | 'truelark_business_id_configured'
  | 'truelark_business_id_missing'
  // ten-dlc-registered
  | 'ten_dlc_no_registration'
  | 'ten_dlc_registration_pending'
  | 'ten_dlc_unregistered'
  | 'ten_dlc_registration_verified'
  // business-preferences
  | 'business_preferences_configured'
  | 'business_profile_fields_missing'
  | 'preferences_fetch_failed'
  | 'scheduling_fields_missing'
  | 'ai_missed_call_text_missing'
  // scheduling-service-type
  | 'scheduling_service_type_configured'
  | 'scheduling_service_type_missing'
  // provider-operatory-available
  | 'scheduling_provider_missing'
  | 'scheduling_operatory_missing'
  | 'scheduling_availability_rule_missing'
  | 'scheduling_config_complete'
  // phone-office-hours
  | 'office_hours_missing'
  | 'office_hours_possibly_default'
  | 'office_hours_configured'
  // ai-missed-call-text
  | 'missed_call_first_message_missing'
  | 'missed_call_air_rule_missing'
  | 'ai_missed_call_text_configured'
  // voice-ai-routing
  | 'org_id_missing'
  | 'tenant_id_missing'
  | 'ai_voice_routing_missing'
  | 'ai_voice_greeting_missing'
  | 'ai_voice_placeholder_missing'
  | 'ai_voice_no_phone_numbers'
  | 'ai_voice_routing_configured'
  // routing-forwarding-activity
  | 'air_routing_activity_healthy'
  | 'air_routing_activity_low'
  | 'air_routing_activity_insufficient'
  // voice-ai-tasks-reviewed
  | 'voice_ai_tasks_reviewed'
  | 'voice_ai_tasks_pending'
  // generic errors
  | 'api_request_failed'
  | 'provider_schedule_fetch_failed'
  | 'operatory_fetch_failed'
  | 'schedules_fetch_failed'
  | 'office_config_fetch_failed'
  | 'business_settings_fetch_failed'
  | 'auto_message_rules_fetch_failed';

// Client-side reason-to-message mapping for the readonly snapshot view.
export const REASON_MESSAGE_MAP: Record<VoiceAirReasonKey, string> = {
  // WAM Customization flags
  air_customization_flags_active:
    'Both "AI Voice" and "Auto Missed Call Message" flags are active.',
  ai_voice_cf_inactive: '"AI Voice" customization flag is not active.',
  auto_missed_call_message_cf_inactive:
    '"Auto Missed Call Message" customization flag is not active.',
  missed_call_notification_ff_inactive:
    '"missed-call-notification-feature-enabled" feature flag is not enabled.',

  // Voice AI tasks
  voice_ai_tasks_reviewed: 'At least 3 voice AI tasks were reviewed in the last 7 days.',
  voice_ai_tasks_pending:
    'Fewer than 3 voice AI tasks were reviewed in the last 7 days. Recent AI tasks may need review before handoff.',

  // TrueLark Business ID
  truelark_business_id_configured: 'TrueLark Business ID is configured.',
  truelark_business_id_missing: 'TrueLark Business ID is missing.',

  // 10DLC registration
  ten_dlc_no_registration: 'No registration details found for this organization.',
  ten_dlc_registration_pending: '10DLC registration is pending.',
  ten_dlc_unregistered: '10DLC registration is unregistered.',
  ten_dlc_registration_verified: '10DLC registration is fully verified.',

  // Business preferences
  business_preferences_configured: 'Business preferences are fully configured.',
  business_profile_fields_missing: 'Required business profile fields are missing.',
  preferences_fetch_failed: 'Preferences not found or failed to load.',
  scheduling_fields_missing: 'Missing scheduling fields.',
  ai_missed_call_text_missing:
    'Auto missed call message text is missing from preferences.',

  // Scheduling Service Type
  scheduling_service_type_configured: 'Scheduling service type is configured.',
  scheduling_service_type_missing: 'Scheduling service type is missing.',

  // Provider Operatory Available
  scheduling_provider_missing: 'TrueLark provider is missing.',
  scheduling_operatory_missing: 'TrueLark operatory is missing.',
  scheduling_availability_rule_missing: 'TrueLark availability rule is missing.',
  scheduling_config_complete: 'Provider, operatory, and availability rules are complete.',

  // Phone Office Hours
  office_hours_missing: 'Office hours are missing.',
  office_hours_possibly_default: 'Office hours might be default.',
  office_hours_configured: 'Office hours are properly configured.',

  // AI Missed Call Text
  missed_call_first_message_missing: 'First message for missed calls is missing.',
  missed_call_air_rule_missing: 'Auto missed call message rule is missing.',
  ai_missed_call_text_configured:
    'Auto Missed Call message and rules are properly set up.',

  // Voice AI Routing
  org_id_missing: 'Organization ID is missing.',
  tenant_id_missing: 'Tenant ID is missing.',
  ai_voice_routing_missing: 'Voice AI routing configuration is missing.',
  ai_voice_greeting_missing: 'Voice AI Greeting is missing.',
  ai_voice_placeholder_missing: 'Voice AI Placeholder script is missing.',
  ai_voice_no_phone_numbers: 'Voice AI phone numbers are missing.',
  ai_voice_routing_configured: 'Voice AI routing and greetings are configured.',

  // Routing Forwarding Activity
  air_routing_activity_healthy: 'Voice AI call activity is healthy.',
  air_routing_activity_low: 'Voice AI call activity is low.',
  air_routing_activity_insufficient: 'Voice AI call activity is insufficient.',

  // Generic errors
  api_request_failed: 'API request failed.',
  provider_schedule_fetch_failed: 'Failed to fetch provider schedule details.',
  operatory_fetch_failed: 'Failed to fetch operatory details.',
  schedules_fetch_failed: 'Failed to fetch schedules.',
  office_config_fetch_failed: 'Failed to fetch office configurations.',
  business_settings_fetch_failed: 'Failed to fetch business settings.',
  auto_message_rules_fetch_failed: 'Failed to fetch auto-message rules.',
};

export const mapSnapshotMetricsToVoiceAirChecklist = (
  metrics: HandoffMetric[],
  locationId: string
): ChecklistMetricItem[] =>
  mapSnapshotMetricsToChecklist(
    metrics,
    VOICE_AIR_METRIC_DEFINITIONS,
    (m, configCriterion) => {
      const reasonKey = m.actualValue as string;
      const resolvedWhereToFix = configCriterion?.whereToFix
        ? {
            label: configCriterion.whereToFix.label,
            url:
              typeof configCriterion.whereToFix.url === 'function'
                ? configCriterion.whereToFix.url({ locationId } as MetricEvalContext)
                : configCriterion.whereToFix.url,
          }
        : undefined;

      return {
        description: m.subsection,
        status: mapResultToStatus(m.result),
        reason: reasonKey,
        message:
          REASON_MESSAGE_MAP[reasonKey as VoiceAirReasonKey] || reasonKey || undefined,
        exception: m.exception,
        whereToFix: resolvedWhereToFix,
      };
    }
  );
